import type { ProjectileLaunch } from "odyssey-shared";

/**
 * What the projectile view reports (P2.4-T22), through the game report's `projectiles`: how many flights, bursts and
 * rings show, how many sprites each pool holds, what became of the launches, the looks the page holds and those an
 * ability named that it lacks, and, on request only, every flight and burst showing. `launch` hands the view launches
 * as a frame would, for a browser suite; it is null before the world has loaded.
 */

/**
 * One flight or burst as a browser suite reads it: where its sprite is drawn in world pixels, its depth, its texture
 * and frame, its turn in radians, whether it is mirrored, for a flight its ring and its shadow when they show and the
 * time left until it lands (for a burst, the time since it landed, negative). A flight also reads what its landing was
 * worked out from, so a suite can hold the page to the rule and not to a machine's timing: its flight and its age in
 * ticks as the launch carried them, when the launch arrived and when the page lands it, both on the page's clock (for
 * a burst, 0, 0 and the landing time twice).
 */
export interface FlightReading {
  readonly kind: "flight" | "burst";
  readonly id: number;
  readonly ability: number;
  readonly x: number;
  readonly y: number;
  readonly depth: number;
  readonly texture: string;
  readonly frame: number;
  readonly rotation: number;
  readonly mirrored: boolean;
  readonly ring: { readonly x: number; readonly y: number; readonly width: number; readonly alpha: number; readonly depth: number; readonly texture: string } | null;
  readonly shadow: { readonly x: number; readonly y: number } | null;
  readonly landsInMs: number;
  readonly flightTicks: number;
  readonly age: number;
  readonly arrivalMs: number;
  readonly landsAtMs: number;
}

/**
 * The view's report. `launched` counts the launches that started a flight, `replaced` those whose id was in flight,
 * `ignored` those sent again while their burst played, `recycled` the flights and bursts taken for a newer one while
 * they showed, and `undrawn` the launches of an ability whose look the page lacks. `roundTripMs` reads the smoothed
 * round trip a launch arriving now would be landed early by (ADR-095 decision 17), null while the page has measured
 * none.
 */
export interface ProjectileReport {
  flights: number;
  bursts: number;
  rings: number;
  flightSprites: number;
  ringSprites: number;
  burstSprites: number;
  launched: number;
  replaced: number;
  ignored: number;
  recycled: number;
  undrawn: number;
  looks: () => readonly string[];
  lacking: () => readonly string[];
  list: () => readonly FlightReading[];
  roundTripMs: () => number | null;
  launch: ((launches: readonly Readonly<ProjectileLaunch>[], rttMs: number | null) => void) | null;
}

/**
 * A report before the world has loaded.
 *
 * @returns The report.
 */
export function createProjectileReport(): ProjectileReport {
  return { flights: 0, bursts: 0, rings: 0, flightSprites: 0, ringSprites: 0, burstSprites: 0, launched: 0, replaced: 0, ignored: 0, recycled: 0, undrawn: 0, looks: () => [], lacking: () => [], list: () => [], roundTripMs: () => null, launch: null };
}
